import { slugify } from './slugify';

export interface ArticleSection {
	/** Slugified id of the section's `## ` heading, or `null` for the preamble. */
	id: string | null;
	/** Raw markdown for this section, including its `## ` heading line. */
	content: string;
}

/** Matches the start (or end) of a fenced code block: ``` or ~~~ at line start. */
const FENCE = /^\s*(```|~~~)/;

/** Matches a level-2 ATX heading line and captures its text. */
const H2 = /^##\s+(.+?)\s*$/;

/**
 * Strip simple inline markdown markers (code, bold, italic) from heading text,
 * EXACTLY like buildToc does, so the derived id matches the TOC id.
 */
const stripInlineMarkers = (text: string): string =>
	text
		.replace(/`([^`]+)`/g, '$1')
		.replace(/\*\*([^*]+)\*\*/g, '$1')
		.replace(/\*([^*]+)\*/g, '$1')
		.replace(/_([^_]+)_/g, '$1')
		.trim();

/**
 * Split an article body into sections delimited by level-2 (`## `) headings.
 *
 * Everything before the first `## ` becomes one preamble section (`id: null`).
 * Each subsequent `## ` heading starts a new section whose id is the slugified,
 * inline-marker-stripped heading text. Headings inside fenced code blocks are
 * ignored. Empty sections are dropped; original line breaks are preserved.
 */
export const splitIntoSections = (body: string): ArticleSection[] => {
	const sections: ArticleSection[] = [];
	let current: { id: string | null; lines: string[] } = { id: null, lines: [] };
	let inFence = false;

	const flush = (): void => {
		if (current.lines.length === 0) return;
		const content = current.lines.join('\n');
		if (content.trim().length === 0) return;
		sections.push({ id: current.id, content });
	};

	for (const line of body.split(/\r?\n/)) {
		if (FENCE.test(line)) {
			inFence = !inFence;
			current.lines.push(line);
			continue;
		}

		if (!inFence) {
			const match = H2.exec(line);
			if (match) {
				flush();
				const id = slugify(stripInlineMarkers(match[1]));
				current = { id, lines: [line] };
				continue;
			}
		}

		current.lines.push(line);
	}

	flush();
	return sections;
};
